import { Component, EventEmitter, Inject, OnInit, Output, Renderer2 } from '@angular/core';
import { map, mergeMap, of, Subject, switchMap, throwError } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { LoaderStatus } from 'libs/core/src/lib/model/loading/loading-status.enum';
import { CheckListItemModel } from 'libs/core/src/lib/model/checklistitem.model';
import { RefundDataProvider } from 'libs/core/src/lib/data-provider/refund.data-provider';
import { NavigationHelperService } from 'libs/core/src/lib/service/navigation/navigation-helper.service';
import { HeaderService } from 'libs/core/src/lib/service/header.service';
import { ScreeningDataProvider } from 'libs/core/src/lib/data-provider/screening.data-provider';
import { ScreenDataProvider } from 'libs/core/src/lib/data-provider/screen.data-provider';
import { ENVIRONMENT_TOKEN } from 'libs/core/src/public-api';
import { GoogleTagManagerService } from 'libs/core/src/lib/service/analytics-services/google-tag-manager.service';
import { StateService } from 'libs/core/src/lib/state/state.service';
import { LoaderService } from 'libs/core/src/lib/service/loading.service';
import { ErrorHandlerService } from 'libs/core/src/lib/service/error-handler/error-handler.service';
import { ActivatedRoute } from '@angular/router';
import { AuthStateService } from 'libs/core/src/lib/state/auth.state.service';
import { FormEventModel } from './form-event.model';
import { ModalStatusEnum } from '../modal/modal-status.enum';
import { CanBeRefundedResponse } from 'libs/core/src/lib/model/response/order-refund.response.models';
import { CanBeRefundedRequest } from 'libs/core/src/lib/model/request/order-refund.request.models';
import { RefundTransactionPaymentResponse } from 'libs/core/src/lib/model/response/refund-transaction.response.model';
import { RefundTransactionRequestModel } from 'libs/core/src/lib/model/request/refund-transaction-payment.request.model';
import { SalesDocumentApiModel } from 'libs/core/src/lib/model/api-model/sales-document/sales-document.api.model';
import { FlowService } from 'libs/core/src/lib/service/flow.service';

@Component({
  template: ''
})
export class BaseRefundComponent {
  @Output() loaderStateChange = new EventEmitter<LoaderStatus>();

  public loaderStatus = LoaderStatus.pending;
  public showStepsContainer = false;

  public formEvent = new Subject<FormEventModel>();
  public isSuccessSend = '';

  public allowTimeBeforeScreening: number;

  constructor(
    @Inject(ENVIRONMENT_TOKEN) protected environment: any,
    protected renderer: Renderer2,
    protected refundDataProvider: RefundDataProvider,
    protected navigationHelperService: NavigationHelperService,
    protected translateService: TranslateService,
    protected headerService: HeaderService,
    protected screeningDataProvider: ScreeningDataProvider,
    protected screenDataProvider: ScreenDataProvider,
    protected googleTagManagerService: GoogleTagManagerService,
    protected stateService: StateService,
    protected loaderService: LoaderService,
    protected errorHandlerService: ErrorHandlerService,
    protected route: ActivatedRoute,
    protected authService: AuthStateService,
    protected flowService: FlowService
  ) {
    this.allowTimeBeforeScreening = this.environment.constants?.refundAllowTimeBeforeScreening ?? 30;
    this.formEvent.subscribe((event) => {
      if (event.action === 'validated') {
        this.changeLoaderState(LoaderStatus.pending);
        this.readFormFields(event.form);
      }
    });
  }

  /// To be overridden in derived classes
  readFormFields(form: FormGroup): void {}

  /// To be overridden in derived classes
  afterAcceptModalClickEvent(): void {}

  onNextClick() {
    this.formEvent.next({ action: 'submit', form: null });
  }

  displayModal(type: 'normal' | 'success' | 'fail') {
    this.changeLoaderState(LoaderStatus.stopped);
    this.isSuccessSend = type;
  }

  getCheckedItems(items: CheckListItemModel[]) {
    return items?.length ? items.filter((o) => o.checked) : [];
  }

  onModalClick(event) {
    switch (event.state) {
      case ModalStatusEnum.accept:
        this.changeLoaderState(LoaderStatus.pending);
        this.afterAcceptModalClickEvent();
        break;
      case ModalStatusEnum.close:
        this.isSuccessSend = null;
        break;
      case ModalStatusEnum.open:
        break;
      case ModalStatusEnum.back:
        this.navigationHelperService.goToRepertoire();
        break;
    }
  }

  changeLoaderState(state: LoaderStatus) {
    this.loaderStateChange.next(state);
  }

  canBeRefunded(cinemaId, request: CanBeRefundedRequest) {
    return this.refundDataProvider.canBeRefunded(cinemaId, request);
  }

  refund(cinemaId, email, salesDocumentId, canBeRefundedRequest: CanBeRefundedRequest) {
    return this.refundDataProvider
      .getSalesDocument(cinemaId, salesDocumentId, canBeRefundedRequest)
      .pipe(switchMap((data) => this.refundDataProvider.refund(cinemaId, salesDocumentId, this.createRefundTransactionRequestModel(email, data))));
  }

  mapError(e: any) {
    return { error: this.errorHandlerService.getError(e, null, true) };
  }

  private createRefundTransactionRequestModel(
    email: string,
    data: {
      refundTransactionPaymentResponse: RefundTransactionPaymentResponse[];
      salesDocumentResponseModel: SalesDocumentApiModel;
      canBeRefundedResponse: CanBeRefundedResponse;
    }
  ) {
    let totalRefundValue = data.canBeRefundedResponse.refundTotalValue;
    const refundRequest: RefundTransactionRequestModel = {
      emailTo: email,
      refundToken: data.canBeRefundedResponse.refundToken,
      paymentTypeList: data.refundTransactionPaymentResponse.map((p) => {
        let refundPayment = p.refundPaymentList.find((o) => o.id === p.id);
        if (!refundPayment && p.refundPaymentList.length > 0) {
          refundPayment = p.refundPaymentList[0];
        }

        let refundValue: any;
        ({ refundValue, totalRefundValue } = this.calculateRefundValue(p, totalRefundValue));
        return {
          id: refundPayment.id,
          refundValue,
          cardId: null,
          transactionId: null
        };
      })
    };

    return refundRequest;
  }

  private calculateRefundValue(p: RefundTransactionPaymentResponse, totalRefundValue: number) {
    let refundValue = 0;
    if (p.maxRefundValue < totalRefundValue) {
      refundValue = p.maxRefundValue;
      totalRefundValue -= p.maxRefundValue;
    } else {
      refundValue = totalRefundValue;
    }
    return { refundValue, totalRefundValue };
  }
}
